/**
 * Universal hero: kits + center stack; under kits — names flanking kickoff time / minute / final.
 */

import React, { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ChevronLeft } from 'lucide-react-native';
import { SoccerKit } from '../../ui/SoccerKit';
import { useTheme } from '../../../src/theme/useTheme';
import { MatchHubHeroBackground } from './MatchHubHeroBackground';
import { getMatchHubHeroStyles } from './MatchHubHero.styles';
import type { MatchHubMockData } from '../../../src/match/types/matchHub.types';
import { formatTimeOnly } from '../../../helpers/formatDateTime.helper';
import { formatShortMd, resultLabelShort } from './matchHubHero.helper';

export interface MatchHubHeroProps {
  data: MatchHubMockData;
  onBack: () => void;
}

export const MatchHubHero: React.FC<MatchHubHeroProps> = ({ data, onBack }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubHeroStyles(theme), [theme]);

  const dateMd = formatShortMd(data.kickoffIso);
  const kickTime = formatTimeOnly(data.kickoffIso);
  const roundLine = data.league.roundLabel;

  const centerTop =
    data.phase === 'pre' ? (
      <>
        {dateMd !== '' && <Text style={styles.centerDate}>{dateMd}</Text>}
        <Text style={styles.centerPrimary}>{data.countdownDisplay}</Text>
      </>
    ) : null;

  const liveTop =
    data.phase === 'live' && data.live !== undefined ? (
      <Text style={styles.scoreLarge}>
        {data.live.homeScore} - {data.live.awayScore}
      </Text>
    ) : null;

  const finishedTop =
    data.phase === 'finished' && data.live !== undefined ? (
      <>
        {dateMd !== '' && <Text style={styles.centerDate}>{dateMd}</Text>}
        <Text style={styles.scoreLarge}>
          {data.live.homeScore} - {data.live.awayScore}
        </Text>
      </>
    ) : null;

  let metaBetweenNames: React.ReactNode;
  if (data.phase === 'pre') {
    metaBetweenNames =
      kickTime !== '' ? (
        <Text style={styles.metaBetweenNames}>{kickTime}</Text>
      ) : (
        <View style={styles.metaBetweenNamesPlaceholder} />
      );
  } else if (data.phase === 'live' && data.live !== undefined) {
    metaBetweenNames = (
      <Text style={styles.metaBetweenNames}>{data.live.minute}′</Text>
    );
  } else if (data.phase === 'finished') {
    metaBetweenNames = (
      <Text style={styles.metaBetweenNames}>{resultLabelShort(data.resultLabel)}</Text>
    );
  } else {
    metaBetweenNames = <View style={styles.metaBetweenNamesPlaceholder} />;
  }

  return (
    <View style={styles.hero}>
      <MatchHubHeroBackground />
      <View style={styles.topBar}>
        <View style={styles.topBarSide}>
          <Pressable
            onPress={onBack}
            style={({ pressed }) => [styles.backButton, pressed && styles.backButtonPressed]}
            accessibilityRole="button"
            accessibilityLabel="Go back"
            testID="match-hub-back"
          >
            <ChevronLeft size={22} color={theme.gray[800]} />
          </Pressable>
        </View>
        <View style={styles.topBarCenter}>
          <Text style={styles.leagueTitle} numberOfLines={1}>
            {data.league.name}
          </Text>
          {roundLine !== '' && (
            <Text style={styles.leagueSub} numberOfLines={1}>
              {roundLine}
            </Text>
          )}
        </View>
        <View style={styles.topBarSideRight} />
      </View>

      <View style={styles.matchupBlock}>
        <View style={styles.kitsAndCenterRow}>
          <View style={styles.sideKitSlot}>
            <SoccerKit
              primary={data.home.kit.primary}
              secondary={data.home.kit.secondary}
              type={data.home.kit.type}
              size={42}
            />
          </View>

          <View style={styles.centerLane}>
            {centerTop}
            {liveTop}
            {finishedTop}
          </View>

          <View style={styles.sideKitSlot}>
            <SoccerKit
              primary={data.away.kit.primary}
              secondary={data.away.kit.secondary}
              type={data.away.kit.type}
              size={42}
            />
          </View>
        </View>

        <View style={styles.namesAndMetaRow}>
          <View style={styles.nameSide}>
            <Text style={styles.teamName} numberOfLines={2}>
              {data.home.name}
            </Text>
          </View>
          <View style={styles.nameCenterSlot}>{metaBetweenNames}</View>
          <View style={styles.nameSide}>
            <Text style={styles.teamName} numberOfLines={2}>
              {data.away.name}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};
